import { useEffect, useState } from "react";
import { Lock, Loader2, Sparkles, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuthState } from "@/hooks/useAuthState";
import { useLanguage } from "@/i18n/LanguageContext";
import { useLiteEnabled } from "@/hooks/useLiteEnabled";
import { useVipPricing, type VipTierKey } from "@/hooks/useVipPricing";
import { cn } from "@/lib/utils";
import VipBillingChoiceModal, { type BillingInterval, type VipChoiceTier } from "@/components/mentoring/VipBillingChoiceModal";
import AuthModal from "@/components/AuthModal";
import { betaGateCheckout } from "@/lib/betaGate";
import { startMentoringCheckout } from "@/lib/mentoringCheckout";

interface Tier {
  key: VipTierKey;
  name: string;
  stage: string;
  frequency: string;
  bullets: string[];
  variant: "default" | "highlight" | "dark";
}

const MentoringSalesSection = () => {
  const { lang } = useLanguage();
  const liteEnabled = useLiteEnabled();
  const es = lang === "es";
  const { isAuthenticated, user, isReady } = useAuthState();
  const pricing = useVipPricing(es, "addon");

  const [hasPanel, setHasPanel] = useState(false);
  const [checking, setChecking] = useState(true);
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [modalTier, setModalTier] = useState<Tier | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingTier, setPendingTier] = useState<Tier | null>(null);

  useEffect(() => {
    const check = async () => {
      if (!isReady) return;
      if (!isAuthenticated || !user) {
        setHasPanel(false);
        setChecking(false);
        return;
      }
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      if (roles?.some((r: any) => r.role === "admin")) {
        setHasPanel(true);
        setChecking(false);
        return;
      }
      const { data: subs } = await supabase
        .from("user_module_subscriptions")
        .select("module_key, status")
        .eq("user_id", user.id)
        .eq("status", "active");
      const { data: mc } = await supabase
        .from("module_content")
        .select("module_key, access_type");
      const includedKeys = new Set(
        (mc ?? []).filter((m: any) => m.access_type === "included").map((m: any) => m.module_key)
      );
      const has = (subs ?? []).some((s: any) => includedKeys.has(s.module_key));
      setHasPanel(has);
      setChecking(false);
    };
    check();
  }, [isReady, isAuthenticated, user]);

  const tiers: Tier[] = es
    ? [
        {
          key: "arranque",
          name: "Quincenal",
          stage: "ORDEN Y CONTROL",
          frequency: "2h/mes (1 sesión cada 15 días)",
          bullets: [
            "Mes a mes, sin permanencia. El panel se contrata aparte.",
            "Aclaramos tus ideas y definimos tu plan de acción.",
            "El respaldo profesional que necesitas para avanzar.",
          ],
          variant: "default",
        },
        {
          key: "crucero",
          name: "Semanal",
          stage: "OPTIMIZACIÓN Y ESCALA",
          frequency: "4h/mes (1 sesión semanal)",
          bullets: [
            "Mes a mes, sin permanencia. El panel se contrata aparte.",
            "Apoyo semanal en la toma de decisiones.",
            "Revisión y optimización de tu operativa.",
          ],
          variant: "highlight",
        },
        {
          key: "partner",
          name: "Intensivo",
          stage: "ALTO RENDIMIENTO Y SEGURIDAD",
          frequency: "8h/mes (2 sesiones semanales)",
          bullets: [
            "Mes a mes, sin permanencia. El panel se contrata aparte.",
            "Respaldo directivo continuo.",
            "Línea directa para urgencias e imprevistos críticos del negocio.",
          ],
          variant: "dark",
        },
      ]
    : [
        {
          key: "arranque",
          name: "Biweekly",
          stage: "ORDER & CONTROL",
          frequency: "2h/mo (1 session every 15 days)",
          bullets: [
            "Month to month, no commitment. The panel is purchased separately.",
            "We clarify your ideas and define your action plan.",
            "The professional backing you need to move forward.",
          ],
          variant: "default",
        },
        {
          key: "crucero",
          name: "Weekly",
          stage: "OPTIMIZATION & SCALE",
          frequency: "4h/mo (1 weekly session)",
          bullets: [
            "Month to month, no commitment. The panel is purchased separately.",
            "Weekly support in decision-making.",
            "Review and optimization of your operations.",
          ],
          variant: "highlight",
        },
        {
          key: "partner",
          name: "Intensive",
          stage: "HIGH PERFORMANCE & SECURITY",
          frequency: "8h/mo (2 weekly sessions)",
          bullets: [
            "Month to month, no commitment. The panel is purchased separately.",
            "Continuous executive-level backing.",
            "Direct line for emergencies and critical business incidents.",
          ],
          variant: "dark",
        },
      ];

  const openModal = (tier: Tier) => {
    if (!isAuthenticated) {
      setPendingTier(tier);
      setAuthOpen(true);
      toast({
        title: es ? "Inicia sesión" : "Sign in",
        description: es
          ? "Necesitas iniciar sesión para activar tu mentoría."
          : "You need to sign in to activate your mentoring.",
      });
      return;
    }
    setModalTier(tier);
  };

  // After successful login, auto-open the billing modal for the pending tier
  useEffect(() => {
    if (isAuthenticated && pendingTier) {
      setAuthOpen(false);
      setModalTier(pendingTier);
      setPendingTier(null);
    }
  }, [isAuthenticated, pendingTier]);

  const handleConfirm = async (interval: BillingInterval) => {
    if (!modalTier) return;
    setLoadingKey(modalTier.key);
    try {
      // Users WITH Panel (paid, promo, manual or admin) → only the proportional
      // mentoring price (280/480/800). Users WITHOUT Panel → combined product.
      if (!hasPanel && (await betaGateCheckout({ plan: "mentoring", interval, lang }))) return;

      const data = await startMentoringCheckout({
        hasPanel,
        tierKey: modalTier.key,
        interval,
      });
      if (data.error) throw new Error(data.error);
      if (data?.url) {
        window.open(data.url, "_blank");
      } else if (data?.success) {
        toast({
          title: es ? "Mentoría activada" : "Mentoring activated",
          description: es
            ? "Se ha añadido a tu suscripción actual. Recibirás la factura por email."
            : "Added to your current subscription. You'll receive the invoice by email.",
        });
      } else {
        throw new Error("No checkout URL");
      }
      setModalTier(null);
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || (es ? "No se pudo iniciar el pago" : "Could not start checkout"),
        variant: "destructive",
      });
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <section className="space-y-10 font-body">
      {/* Header */}
      <div className="text-center space-y-4 max-w-3xl mx-auto">
        <h2 className="font-body text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight tracking-tight text-balance mb-2">
          {es
            ? "Deja de estar solo. Déjate asesorar por nuestros expertos."
            : "Stop going it alone. Let our experts advise you."}
        </h2>
        <p className="font-body text-base sm:text-lg text-muted-foreground leading-relaxed">
          {es
            ? `Tu panel (${liteEnabled ? "Lite, " : ""}Básico, Club Pro o Avanzado) te da los datos. El Método Lemon con Tu Mentor te da el mapa para ejecutarlos. Elige tu ritmo de crecimiento.`
            : `Your panel (${liteEnabled ? "Lite, " : ""}Basic, Club Pro or Advanced) gives you the data. The Lemon Method with Your Mentor gives you the map. Choose your growth pace.`}
        </p>
        <div className="flex flex-col items-center gap-2 pt-1">
          <Badge variant="outline" className="gap-1.5 border-primary/40 bg-primary/10 text-foreground text-xs font-medium px-3 py-1">
            <Lock className="w-3 h-3" />
            {es
              ? "La mentoría es un extra mensual: el panel se contrata aparte"
              : "Mentoring is a monthly extra: the panel is purchased separately"}
          </Badge>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
        {tiers.map((tier) => {
          const isHighlight = tier.variant === "highlight";
          const loading = loadingKey === tier.key;
          const guest = !isAuthenticated && isReady;

          return (
            <div
              key={tier.key}
              className={cn(
                "relative rounded-2xl p-8 sm:p-10 flex flex-col gap-5 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 duration-300 bg-card",
                isHighlight ? "border-2 border-primary ring-4 ring-primary/20" : "border border-border"
              )}
            >
              {isHighlight && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground font-bold gap-1 px-3 py-1 shadow-md">
                  <Sparkles className="w-3 h-3" />
                  {es ? "RECOMENDADO" : "RECOMMENDED"}
                </Badge>
              )}

              <div className="space-y-3">
                <p className="font-body text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                  {tier.stage}
                </p>
                <h3 className="font-body text-2xl font-extrabold tracking-tight text-foreground">
                  {tier.name}
                </h3>
                <p className="font-body text-4xl font-extrabold tracking-tight text-foreground">
                  {pricing.tiers[tier.key].monthly.label}
                  <span className="font-body text-sm font-normal ml-1 text-muted-foreground">
                    {es ? "/mes" : "/mo"}
                  </span>
                </p>
              </div>

              <p className="font-body text-sm text-slate-500 leading-relaxed">
                {(() => {
                  const parts = tier.frequency.split(" · ");
                  return (
                    <>
                      <span className="block">{parts[0]}</span>
                      {parts[1] && <span className="block">{parts[1]}</span>}
                    </>
                  );
                })()}
              </p>

              <ul className="space-y-3 flex-1">
                {tier.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 font-body text-sm text-foreground/90 leading-snug">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-foreground" strokeWidth={2.5} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <Button
                size="lg"
                disabled={loading || checking}
                onClick={() => openModal(tier)}
                className="w-full font-bold rounded-xl transition-all mt-2 bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/40"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <span className="whitespace-nowrap">
                    {hasPanel
                      ? es
                        ? `Añadir Mentoría ${tier.name}`
                        : `Add ${tier.name} Mentoring`
                      : guest || !hasPanel
                      ? es
                        ? `Añadir Mentoría ${tier.name}`
                        : `Add ${tier.name} Mentoring`
                      : ""}
                  </span>
                )}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Disclaimer condiciones generales */}
      <p className="text-center font-body text-xs text-slate-500 w-full leading-relaxed max-w-3xl mx-auto">
        {es
          ? "Las sesiones tienen una duración estricta de 1 hora para asegurar la máxima productividad. Cancelaciones con 24h de antelación."
          : "Sessions have a strict 1-hour duration to ensure maximum productivity. Cancellations require 24h notice."}
      </p>

      {/* Disclaimer legal */}
      <p className="text-center font-body text-xs text-muted-foreground w-full leading-relaxed">
        <strong className="text-foreground font-semibold">
          {es ? "Transparencia Lemon: " : "Lemon Transparency: "}
        </strong>
        {es
          ? "Sin permanencia: la mentoría y los planes del panel son mensuales y puedes cancelar cuando quieras. La mentoría no incluye ningún plan del panel; verás el precio antes de pagar."
          : "No commitment: mentoring and panel plans are monthly and you can cancel anytime. Mentoring does not include any panel plan; you'll see the price before paying."}
      </p>

      <VipBillingChoiceModal
        open={!!modalTier}
        onOpenChange={(o) => !o && setModalTier(null)}
        tier={
          modalTier
            ? ({
                key: modalTier.key,
                name: modalTier.name,
                mentorMonthlyCents: Math.max(
                  0,
                  pricing.tiers[modalTier.key].monthly.cents,
                ),
                mentorAnnualCents: Math.max(
                  0,
                  pricing.tiers[modalTier.key].annual.cents,
                ),
                panelMonthlyCents: 9900,
                panelAnnualCents: 99000,
              } as VipChoiceTier)
            : null
        }
        hasPanel={hasPanel}
        es={es}
        loading={loadingKey !== null}
        onConfirm={handleConfirm}
      />
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} redirectTo="/mentoria-retail" />
    </section>
  );
};

export default MentoringSalesSection;
